import { Component, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { FiAlertTriangle, FiRefreshCw, FiHome } from 'react-icons/fi';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  override render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex min-h-screen items-center justify-center bg-surface px-4">
          <div className="w-full max-w-md text-center">
            <div className="mb-6 flex justify-center">
              <div className="rounded-full bg-critical-soft p-6">
                <FiAlertTriangle className="h-16 w-16 text-critical" aria-hidden="true" />
              </div>
            </div>

            <h1 className="mb-3 text-2xl font-bold text-content">Something Went Wrong</h1>

            <p className="mb-2 text-content-muted">
              An unexpected error occurred. Don&apos;t worry, your data is safe.
            </p>

            {import.meta.env.DEV && this.state.error && (
              <div className="my-6 rounded-surface border border-border bg-surface-sunken p-4 text-left">
                <p className="mb-2 text-sm font-medium text-content">Error Details:</p>
                <pre className="overflow-x-auto text-xs text-content-muted">
                  {this.state.error.message}
                </pre>
              </div>
            )}

            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
              <button
                onClick={this.handleReset}
                className="inline-flex items-center justify-center gap-2 rounded-control border border-accent bg-accent px-4 py-2 text-sm font-medium text-accent-content transition-colors hover:opacity-90"
              >
                <FiRefreshCw className="h-4 w-4" aria-hidden="true" />
                Try Again
              </button>

              <Link
                to="/overview"
                className="inline-flex items-center justify-center gap-2 rounded-control border border-border bg-surface px-4 py-2 text-sm font-medium text-content transition-colors hover:bg-surface-raised"
              >
                <FiHome className="h-4 w-4" aria-hidden="true" />
                Go to Dashboard
              </Link>
            </div>

            <div className="mt-8 text-sm text-content-muted">
              If this problem persists, please contact support.
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
